// The org's API keys (API-010, REQ-013) and the one-time secret dialog they share with the billing
// admin's installation secrets: the issue form's rules, its request, the typed-name label and
// which key may be revoked. Moved from the AllCode admin's `screens/orgs/model.ts` (REQ-240) so
// both shells issue keys from one screen. Pure and DOM-free; a key's secret lives only in the
// dialog's state.
import type { components } from "../../api/schema";
import { hasErrors, validate, type FieldErrors, type Rule } from "../../primitives/forms";
import { personName } from "../quotas/names";

export type Credential = components["schemas"]["Credential"];
export type CreateCredentialRequest = components["schemas"]["CreateCredentialRequest"];
export type CredentialKind = components["schemas"]["CredentialKind"];
type User = components["schemas"]["User"];

// ------------------------------------------------------------------------- the one-time secret

export interface SecretDialogState {
  secret: string;
  acknowledged: boolean;
}

export function openSecretDialog(secret: string): SecretDialogState {
  return { secret, acknowledged: false };
}

export function acknowledgeSecretDialog(state: SecretDialogState): SecretDialogState {
  return { ...state, acknowledged: true };
}

/** The dialog cannot be dismissed (Esc, backdrop, close) until acknowledged, or was never opened. */
export function canDismissSecretDialog(state: SecretDialogState | null): boolean {
  return state === null || state.acknowledged;
}

// ---------------------------------------------------------------------------------- API keys

export const CREDENTIAL_MAX_EXPIRY_DAYS = 365;
export const SERVICE_CREDENTIAL_SCOPES = ["reports:read", "prompts:read", "admin:write"] as const;

/** A mandatory expiry, at most `CREDENTIAL_MAX_EXPIRY_DAYS` ahead of `now` (ADR-002). */
export function expiryRule(now: Date): Rule {
  return (value) => {
    const trimmed = value.trim();
    if (trimmed === "") return "Required.";
    const time = Date.parse(trimmed);
    if (Number.isNaN(time)) return "A valid date.";
    if (time <= now.getTime()) return "Must be in the future.";
    const maxMs = now.getTime() + CREDENTIAL_MAX_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    return time > maxMs ? `At most ${CREDENTIAL_MAX_EXPIRY_DAYS} days ahead.` : null;
  };
}

export interface NewCredentialValues {
  kind: CredentialKind;
  userId: string;
  expiresAt: string;
  scopes: readonly string[];
}

export function validateNewCredential(values: NewCredentialValues, now: Date): FieldErrors {
  const errors = validate({ expiresAt: values.expiresAt }, { expiresAt: [expiryRule(now)] });
  if (values.kind === "user_proxy" && values.userId.trim() === "") errors["userId"] = "Required for a user credential.";
  if (values.kind === "service" && values.userId.trim() !== "") errors["userId"] = "Not allowed for a service credential.";
  if (values.kind === "service") {
    const unknown = values.scopes.filter((scope) => !(SERVICE_CREDENTIAL_SCOPES as readonly string[]).includes(scope));
    if (unknown.length > 0) errors["scopes"] = `Unknown scope: ${unknown.join(", ")}.`;
  } else if (values.scopes.length > 0) {
    errors["scopes"] = "Not allowed for a user credential.";
  }
  return errors;
}

export function newCredentialRequest(values: NewCredentialValues, now: Date): CreateCredentialRequest | null {
  if (hasErrors(validateNewCredential(values, now))) return null;
  const request: CreateCredentialRequest = { kind: values.kind, expiresAt: new Date(values.expiresAt.trim()).toISOString() };
  if (values.kind === "user_proxy") request.userId = values.userId.trim();
  if (values.kind === "service" && values.scopes.length > 0) request.scopes = [...values.scopes];
  return request;
}

/** What the typed-name confirmation names, since a credential has no human name of its own. */
export function credentialLabel(credential: Credential): string {
  return `${credential.kind === "service" ? "service" : "user"} API key ${credential.prefix}`;
}

export function canRevokeCredential(credential: Credential): boolean {
  return credential.revokedAt === null;
}

/** One choice of the issue dialog's user picker: the id is sent, only the label is shown. */
export interface UserOption {
  id: string;
  label: string;
}

/** Active users by `personName`, the email beside a name that is not itself one; never the
 *  external id as it is (IA-1). */
export function userOptions(users: readonly User[]): UserOption[] {
  return users
    .filter((user) => user.status === "active")
    .map((user) => {
      const name = personName(user);
      const detail = user.email?.trim() && user.email.trim() !== name ? user.email.trim() : null;
      return { id: user.id, label: detail ? `${name} (${detail})` : name };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}
